import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Grid, Chip, LinearProgress, Alert, CircularProgress, Divider,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractService } from '@smartgrow/shared';
import type { Contract } from '@smartgrow/shared';

const STATUS_COLORS: Record<string, 'success' | 'default' | 'warning' | 'error'> = {
  ACTIVE: 'success',
  COMPLETED: 'default',
  SUSPENDED: 'warning',
  TERMINATED: 'error',
};

const fmt = (n?: number) => n == null ? '—' : `₦${Number(n).toLocaleString('en-NG')}`;

const Field = ({ label, value, onClick }: { label: string; value: React.ReactNode; onClick?: () => void }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography
      variant="body1"
      color={onClick ? 'primary' : undefined}
      sx={onClick ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : undefined}
      onClick={onClick}
    >
      {value ?? '—'}
    </Typography>
  </Grid>
);

export const ContractDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const contractId = id!;

  const { data: contract, isLoading, isError } = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => contractService.getById(contractId),
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) => contractService.updateStatus(contractId, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contract', contractId] }),
  });

  if (isLoading) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
  if (isError || !contract) return <Alert severity="error">Contract not found.</Alert>;

  const c = contract as Contract;
  const progress = Math.min(Number(c.paymentProgress ?? 0), 100);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>Back</Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold">{c.contractNumber}</Typography>
          <Typography
            variant="body2" color="primary"
            sx={{ cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate(`/dashboard/projects/${c.projectId}`)}
          >
            {c.projectName}
          </Typography>
        </Box>
        <Chip label={c.status} color={STATUS_COLORS[c.status] ?? 'default'} />
        {c.status === 'ACTIVE' && (
          <Button variant="outlined" color="warning" size="small"
            disabled={statusMutation.isPending}
            onClick={() => statusMutation.mutate('SUSPENDED')}>
            Suspend
          </Button>
        )}
        {c.status === 'SUSPENDED' && (
          <Button variant="outlined" color="success" size="small"
            disabled={statusMutation.isPending}
            onClick={() => statusMutation.mutate('ACTIVE')}>
            Reinstate
          </Button>
        )}
        {(c.status === 'ACTIVE' || c.status === 'SUSPENDED') && (
          <Button variant="outlined" color="error" size="small"
            disabled={statusMutation.isPending}
            onClick={() => statusMutation.mutate('TERMINATED')}>
            Terminate
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Contract Details</Typography>
            <Grid container spacing={2}>
              <Field label="Contract Number" value={c.contractNumber} />
              <Field label="Contractor" value={c.contractorCompanyName} />
              <Field label="Project" value={c.projectName} onClick={() => navigate(`/dashboard/projects/${c.projectId}`)} />
              <Field label="Contract Value" value={fmt(c.contractValue)} />
              <Field label="Amount Paid" value={fmt(c.paidAmount)} />
              <Field label="Pending Amount" value={fmt(c.pendingAmount)} />
              <Field label="Start Date" value={c.startDate} />
              <Field label="End Date" value={c.endDate} />
              <Field label="Created By" value={c.createdByName} />
            </Grid>
            {c.description && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{c.description}</Typography>
              </>
            )}
            {c.notes && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{c.notes}</Typography>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Payment Progress</Typography>
            <Typography variant="h3" fontWeight="bold" color={progress === 100 ? 'success.main' : 'primary.main'}>
              {progress.toFixed(0)}%
            </Typography>
            <LinearProgress
              variant="determinate" value={progress}
              color={progress === 100 ? 'success' : 'primary'}
              sx={{ my: 1, height: 8, borderRadius: 4 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Paid</Typography>
                <Typography fontWeight="medium">{fmt(c.paidAmount)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">Remaining</Typography>
                <Typography fontWeight="medium">{fmt(c.pendingAmount)}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
